import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { captureException } from "@sentry/react-native";

import {
  FulfillmentOption,
  LatLng,
  MapData,
  PickupAddress,
  PickupSchedule,
  ReadyForPickupEstimate,
  FulfillmentDispatchType,
} from "../types";

type ContextAPI = {
  manualFulfillmentsEnabled: boolean;
  automaticFulfillmentsEnabled: boolean;
  onDemandFulfillmentsFeatureEnabled: boolean;
  updateAutomaticFulfillmentFeatureEnabled: (enabled: boolean) => Promise<void>;

  updateAutomaticFulfillment: (
    type: FulfillmentDispatchType,
    enabled: boolean
  ) => Promise<void>;

  fulfillmentOptions: FulfillmentOption[];
  fetchFulfillmentOptions: () => Promise<void>;

  savePickupAddress: (pickupAddress: PickupAddress) => Promise<PickupAddress>;
  getPickupAddress: () => Promise<PickupAddress>;
  getPickupSchedules: (params: LatLng) => Promise<PickupSchedule[]>;

  getReadyForPickupEstimate: () => Promise<ReadyForPickupEstimate>;
  saveReadyForPickupEstimate: (
    hours: string
  ) => Promise<ReadyForPickupEstimate>;

  navigateToLegacyFulfillment: () => void;
};

type ContextType = ContextAPI & {
  pickupAddress: PickupAddress | null;
  pickupSchedules: PickupSchedule[] | null;
  readyForPickupEstimate: ReadyForPickupEstimate | null;
  selectedMapData: MapData | null;
  setSelectedMapData: (data: MapData | null) => void;
};
export type { ContextAPI as TDeliveryOptions };

const Context = createContext<ContextType | null>(null);

export default function useDeliveryFulfillments() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("Missing DeliveryFulfillmentsProvider");
  return ctx;
}

export function DeliveryFulfillmentsProvider({
  children,
  ...props
}: PropsWithChildren<ContextAPI>) {
  const [pickupSchedules, setPickupSchedules] = useState<
    PickupSchedule[] | null
  >(null);

  const [pickupAddress, setPickupAddress] = useState<PickupAddress | null>(
    null
  );

  const [readyForPickupEstimate, setReadyForPickupEstimate] =
    useState<ReadyForPickupEstimate | null>(null);

  const [selectedMapData, setSelectedMapData] = useState<MapData | null>(null);

  const {
    fetchFulfillmentOptions,
    getPickupAddress,
    getPickupSchedules,
    getReadyForPickupEstimate,
  } = props;

  const latitude = pickupAddress?.geolocation.latitude;
  const longitude = pickupAddress?.geolocation.longitude;

  useEffect(() => {
    fetchFulfillmentOptions();
    getPickupAddress().then(setPickupAddress).catch(captureException);
    getReadyForPickupEstimate()
      .then(setReadyForPickupEstimate)
      .catch(captureException);

    async function fetchPickupAddress() {
      try {
        const address = await getPickupAddress();
        setPickupAddress({ ...address, country_code: null });
      } catch (error) {
        captureException(error);
      }
    }

    fetchPickupAddress();

    if (latitude && longitude) {
      getPickupSchedules({ latitude, longitude })
        .then(setPickupSchedules)
        .catch(captureException);
    }
  }, [
    latitude,
    longitude,
    fetchFulfillmentOptions,
    getPickupAddress,
    getPickupSchedules,
    getReadyForPickupEstimate,
  ]);

  async function savePickupAddress(pickupAddress: PickupAddress) {
    const savedPickupAddress = await props.savePickupAddress(pickupAddress);
    setPickupAddress(savedPickupAddress);
    return savedPickupAddress;
  }

  async function saveReadyForPickupEstimate(hours: string) {
    const estimate = await props.saveReadyForPickupEstimate(hours);
    setReadyForPickupEstimate(estimate);
    return estimate;
  }

  async function updateAutomaticFulfillment(
    type: FulfillmentDispatchType,
    enabled: boolean
  ) {
    await props.updateAutomaticFulfillment(type, enabled);
    await fetchFulfillmentOptions();
  }

  return (
    <Context.Provider
      value={{
        ...props,
        pickupAddress,
        pickupSchedules,
        readyForPickupEstimate,
        updateAutomaticFulfillment,
        saveReadyForPickupEstimate,
        savePickupAddress,
        selectedMapData,
        setSelectedMapData,
      }}
    >
      {children}
    </Context.Provider>
  );
}
